import type { EventId, WorldDateView } from "../world/types";

/*
 * The player's journal: what they learned, and how they learned it.
 *
 * Client-side state, and it is client-side on purpose (docs/plans/phase-3-plan.md,
 * decision 10): the player is not a simulation entity, nothing they do in Phase 3
 * mutates the world, and a knowledge store that lived in `sim-core` would be a
 * hashed record of a thing the simulation has no opinion about.
 *
 * The one rule the store is built around is the same one the rumour tier is built
 * around — **an entry is a reference, never a copy**. A journal entry holds an
 * {@link EventId} plus provenance, and the sentence it kept is a note about what
 * the player was *told*, not a second copy of the record. That is what makes the
 * phase's exit criterion checkable rather than assertable: three channels have
 * genuinely carried the same war when three entries hold the same `event`, and a
 * debug view can put that id back through `WitnessSource.describe` and read the
 * chronicler's own line for it (`world/types.ts`).
 *
 * There is no DOM and no Three.js here. The overlay that draws a journal lives in
 * `hud/panels.ts`; this file is the state and the rules over it, which is what
 * makes it checkable in `dev/selfcheck.ts`.
 */

/**
 * How a piece of news reached the player.
 *
 * The four diegetic channels Phase 3 opens, and they are the unit the exit
 * criterion is counted in ("at least three independent diegetic channels — a
 * refugee, a price, an overheard line or grave", roadmap Phase 3). `"grave"` is
 * the ground speaking generally: a headstone, a burned farmstead and a
 * battlefield marker are one channel, because they are one mechanism —
 * aftermath derived from the log and read where it stands (plan decision 6).
 *
 * Phase 4 adds the fifth: `"caravan"`, which is the *word* given a body (plan
 * decision 2). It is its own channel rather than another `"npc"` for the reason
 * the exit criterion counts channels at all — what a trader knows is what
 * somewhere else knows, and hearing the same record from a townsman and from a
 * caravan out of the town it happened in is genuinely two independent ways it
 * reached the player.
 *
 * **Nothing writes `"caravan"` today, and the member stays.** The cart the client
 * drew along a road is retired — trade is an abstract pulse the simulation holds
 * no position for — so there is nobody out there to hear anything from until
 * columns put bodies back on the roads, and this is the channel they will file
 * under. What hangs off it is kept with it: the verb below, and
 * `journal/people.ts`'s "met on the road at".
 *
 * Phase 5 adds the sixth: `"book"`, which is the word given a *shelf* (plan
 * decision 5). It is its own channel for the reason `"caravan"` is one — what a
 * chronicle holds is what somebody thought worth writing down years ago, and a
 * record that reached the player off a page as well as out of a mouth reached
 * them two independent ways. It is also the one channel a town runs itself: a
 * settlement's own rumour store learns from the books standing in it, one a
 * month, so a player filing a book is doing what the town does
 * (`world/types.ts`, `WitnessSource.chronicleEntries`).
 */
export type JournalChannel = "npc" | "market" | "grave" | "refugee" | "caravan" | "book";

/** Where an entry came from: the channel, and who or what spoke through it. */
export interface LearnedVia {
  readonly channel: JournalChannel;

  /**
   * The name the player would use for the source: "Halvard Reedmere", "the
   * market board", "a grave", "refugees from Stonehearth".
   *
   * Part of the identity of an entry, not decoration — hearing the same news
   * from two different people is two things the player learned, and the
   * exit criterion counts *independent* channels.
   */
  readonly who: string;
}

/**
 * One record, with every entry the player filed about it.
 *
 * The journal read the way the exit criterion counts it: by *record* rather than
 * by entry, because "three independent diegetic channels have carried news of
 * it" is a statement about one war and the several ways it arrived, and a flat
 * list of entries makes that a thing a reader has to assemble by eye
 * (roadmap Phase 3). {@link Journal.byRecord} builds it; the trace view draws
 * it beside the log's own sentence for the record (`hud/panels.ts`).
 */
export interface JournalRecord {
  readonly event: EventId;

  /** Everything filed about it, in the order it was learned. Never empty. */
  readonly entries: readonly JournalEntry[];
}

/** One thing the player learned, and how. */
export interface JournalEntry {
  /**
   * The record this is about.
   *
   * The whole of what the player knows, in the simulation's own terms. Shown in
   * the overlay as well as stored, because the debug hook the plan asks for is
   * "an entry displays its `eventId` so it is traceable against the sim log".
   */
  readonly event: EventId;

  readonly via: LearnedVia;

  /** The in-game date the player learned it. */
  readonly whenLearned: WorldDateView;

  /**
   * What the player was told, verbatim.
   *
   * A note, not a source of truth: the delivery-side vagueness that
   * `WitnessSource.speak` applied for hops and age is baked into this string,
   * and {@link JournalEntry.event} is what stays true. Nothing reads it except
   * the overlay.
   */
  readonly note: string;
}

/**
 * What the player knows, in the order they came to know it.
 *
 * Insertion order and not sorted by date: a journal is a record of learning, and
 * hearing about an old battle today belongs at the bottom of the page rather
 * than filed back in the year it happened.
 */
export class Journal {
  private readonly list: JournalEntry[] = [];

  /** Identity keys of everything already recorded — see {@link Journal.append}. */
  private readonly seen = new Set<string>();

  /** Everything learned, oldest first. */
  get entries(): readonly JournalEntry[] {
    return this.list;
  }

  get size(): number {
    return this.list.length;
  }

  /**
   * Records something learned, and returns `false` if it was already known.
   *
   * **Identity is `(event, channel, who)`, and the three-part key is the
   * mechanic rather than a detail of it.** Asking one townsman the same question
   * twice must not fill a page, so the same person telling the same news is one
   * entry — but the market board moving on the same war, and a refugee arriving
   * from it, are separate things the player learned and the exit criterion is
   * counted in exactly that unit ("at least three independent diegetic
   * channels", roadmap Phase 3). Deduplicating on `event` alone would erase the
   * thing the phase exists to demonstrate.
   */
  append(entry: JournalEntry): boolean {
    const key = identity(entry.event, entry.via);

    if (this.seen.has(key)) {
      return false;
    }

    this.seen.add(key);
    this.list.push(entry);

    return true;
  }

  /** Whether this exact source has already told the player about this record. */
  has(event: EventId, via: LearnedVia): boolean {
    return this.seen.has(identity(event, via));
  }

  /**
   * The distinct channels that have carried one record.
   *
   * The exit criterion, computed: three of these for one `event` is the phase's
   * claim, and it is a set rather than a count because two graves are still one
   * channel.
   */
  channelsFor(event: EventId): readonly JournalChannel[] {
    const channels: JournalChannel[] = [];

    for (const entry of this.list) {
      if (entry.event === event && !channels.includes(entry.via.channel)) {
        channels.push(entry.via.channel);
      }
    }

    return channels;
  }

  /**
   * Everything learned, grouped by the record it was about.
   *
   * Records in the order they were *first* learned and entries within a record
   * in the order they arrived, so the list reads as "this is what happened, and
   * here is every way it reached me" — which is the sentence the phase's exit
   * criterion is written in. Rebuilt on each call rather than kept alongside the
   * list: a journal is a few dozen entries and the only caller is a panel a
   * player opened.
   */
  byRecord(): readonly JournalRecord[] {
    const grouped = new Map<EventId, JournalEntry[]>();

    for (const entry of this.list) {
      const known = grouped.get(entry.event);

      if (known === undefined) {
        grouped.set(entry.event, [entry]);
      } else {
        known.push(entry);
      }
    }

    return [...grouped].map(([event, entries]) => ({ event, entries }));
  }

  /** Forgets everything. Only the dev handle calls it. */
  clear(): void {
    this.list.length = 0;
    this.seen.clear();
  }
}

/**
 * Renders an entry's provenance the way the plan writes it: "heard from Halvard
 * Reedmere, year 118 day 151".
 *
 * The year is in there and the plan's own phrasing ("heard from <somebody>, day
 * 12") is not, deliberately: a world runs for a century of in-game time before a
 * session starts (plan decision 1), so a bare day number names 336 different
 * days and a player comparing two entries would have no way to tell which came
 * first.
 */
export function provenanceOf(entry: JournalEntry): string {
  return `${verbFor(entry.via.channel)} ${entry.via.who}, ${formatWorldDate(entry.whenLearned)}`;
}

/** Renders a date as "year 24 day 12". The one place the client formats one. */
export function formatWorldDate(date: WorldDateView): string {
  return `year ${date.year} day ${date.dayOfYear}`;
}

/** How the journal says a channel delivered its news. */
function verbFor(channel: JournalChannel): string {
  switch (channel) {
    case "npc":
      return "heard from";
    case "market":
      return "read off";
    case "grave":
      return "read at";
    case "refugee":
      return "heard from";
    case "caravan":
      // The same verb as a townsman's, because it was the same act — the entry
      // is told apart by *who*, and "the caravan from Bramblewick" says it.
      return "heard from";
    case "book":
      // Not "read at", which is the grave's: a stone is read *where it stands*
      // and the place is half of what makes it a channel, where a book is read
      // *in* and the place it happens to be lying is not what it says.
      return "read in";
  }
}

/**
 * The deduplication key. Not parsed anywhere — an identity, not a serialisation.
 *
 * Joined on NUL because a speaker's name is arbitrary text and every printable
 * separator is a name somebody could have: "read off" plus "the market board" and
 * "read" plus "off the market board" would be the same key under a space.
 * Written as the escape rather than as the character itself, because a source
 * file with a raw NUL in it is a file `grep` calls binary and silently skips.
 */
function identity(event: EventId, via: LearnedVia): string {
  return `${event}\0${via.channel}\0${via.who}`;
}
